import { gql, useMutation, useQuery } from "@apollo/client";
import React, { useState } from "react";
import { useHistory, useRouteMatch } from "react-router";
import { BreadCrumb, BreadCrumbProp } from "../../components/light/Breadcrumb";
import { Content } from "../../components/light/Content";
import { SectionBlock } from "../../components/light/SectionBlock";
import { Loading } from "../../components/Loading";
import { Toast } from "../../hooks/toast";
import { useTranslation } from "react-i18next";

export const UPLOAD = gql`
  mutation upload($file: Upload!) {
    upload(file: $file) {
      filename
      url
    }
  }
`;

const Merchant_Brand = gql`
  query merchantBrand($id: Int!) {
    merchantBrand(id: $id) {
      id
      alias
      name
      banner
      picture
      description
      status
    }
  }
`;

const CREATE_BRAND = gql`
  mutation merchantCreateBrand($data: BrandInput) {
    merchantCreateBrand(data: $data)
  }
`;

const UPDATE_BRAND = gql`
  mutation merchantUpdateBrand($id: Int!, $data: BrandInput) {
    merchantUpdateBrand(id: $id, data: $data)
  }
`;

export function FormBrandScreen() {
  const { t } = useTranslation();
  const { params }: any = useRouteMatch();
  const { push } = useHistory();
  const [picture, setPicture] = useState("");
  const [liftstyle, setLiftstyle] = useState("");
  const [alias, setAlias] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const { loading, data } = useQuery(Merchant_Brand, {
    fetchPolicy: "no-cache",
    variables: {
      id: Number(params.id),
    },
    skip: !params.id,
    onCompleted: () => {
      setName(data.merchantBrand.name);
      setAlias(data.merchantBrand.alias);
      setPicture(data.merchantBrand.picture);
      setLiftstyle(data.merchantBrand.banner);
      setDescription(data.merchantBrand.description);
    },
  });
  const [update] = useMutation(params.id ? UPDATE_BRAND : CREATE_BRAND, {
    onCompleted: (res) => {
      if (res.merchantCreateBrand) {
        Toast.onShow("Brand was created", "success", "right");
        push("/options/brand/list");
      }

      if (res.merchantUpdateBrand) {
        Toast.onShow(`Brand ${name} was update`, "success", "right");
        push("/options/brand/list");
      }
    },
  });

  const BreadCrumbItems: BreadCrumbProp[] = [
    { text: "options", to: "#" },
    { text: "brands", to: "#" },
    { text: "my brand", to: "/options/brand/list" },
    {
      text: params.id ? "Edit Brand" : "Create Brand",
      to: !params.id ? "/options/brand/create" : "/options/brand/edit/" + params.id,
    },
  ];

  const handleOnSubmit = (e: any) => {
    e.preventDefault();
    const data = {
      alias,
      name,
      banner: picture,
      picture: liftstyle,
      description,
    };

    if (params.id) {
      update({
        variables: {
          id: Number(params.id),
          data,
        },
      });
    } else {
      update({
        variables: {
          id: Number(params.id),
          data,
        },
      });
    }
  };

  if (loading) {
    return (
      <Content breadcrumb={<BreadCrumb items={BreadCrumbItems} />}>
        <Loading />
      </Content>
    );
  }

  return (
    <Content breadcrumb={<BreadCrumb items={BreadCrumbItems} />}>
      <div className="row">
        <div className="col-md-6">
          <SectionBlock title={`${params.id ? t("edit") + params.id : t("create")} ${t('brand')}`}>
            <form onSubmit={handleOnSubmit}>
              <div className="row">
                <div className="col">
                  <div className="form-group">
                    <label>{t('alias')}</label>
                    <input
                      value={alias}
                      onChange={(e) => setAlias(e.target.value)}
                      type="text"
                      className="form-control form-secondary"
                      placeholder={`${t('enter')} ${t('alias')}`}
                    />
                  </div>
                </div>
                <div className="col">
                  <div className="form-group">
                    <label>{t('name')}</label>
                    <input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      type="text"
                      className="form-control form-secondary"
                      placeholder={`${t('enter')} ${t('name')}`}
                    />
                  </div>
                </div>
              </div>
              <UploadController label={t('upload_picture')} value={picture} onUpload={(e) => setPicture(e)} />
              <UploadController label={t("lifestyle_picture")} value={liftstyle} onUpload={(e) => setLiftstyle(e)} />
              <div className="form-group">
                <label>{t('description')}</label>
                <textarea
                  rows={5}
                  className="form-control form-secondary"
                  placeholder={`${t('enter')} ${t('description')}`}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                ></textarea>
              </div>
              <button type="submit" className="btn btn-primary btn-sm">
                {params.id ? t('edit') : t('create')}
              </button>
            </form>
          </SectionBlock>
        </div>
      </div>
    </Content>
  );
}

function UploadController({ label, value, onUpload }: { label: string; value: string; onUpload: (e: any) => void }) {
  const { t } = useTranslation();
  let refUpload: any;
  const [upload] = useMutation(UPLOAD, {
    onCompleted: (res) => {
      onUpload(res.upload.url);
    },
  });

  const handleUpload = () => {
    refUpload.click();
  };

  const handleFileUpload = (e: any) => {
    console.log(e.target.files[0]);
    upload({
      variables: {
        file: e.target.files[0],
      },
    });
  };

  return (
    <div className="form-group">
      <label>{label}</label>
      <div className="form-control form-secondary" style={{ height: "auto" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <button type="button" className="btn btn-primary btn-sm" onClick={handleUpload} style={{ height: 30 }}>
            {t('choose_file')}
          </button>
          {value && <img src={value} alt="" width={50} height={50} style={{ objectFit: "cover" }} />}
        </div>
      </div>
      <input
        type="file"
        style={{ display: "none" }}
        ref={(ref) => (refUpload = ref)}
        multiple={false}
        onChange={handleFileUpload}
      />
    </div>
  );
}
